'use strict';
const deepmerge = require('deepmerge');
const { gql } = require('graphql-tag');
const defaultConfig = require('./default');

var _parent = {};

var config = {
  init: p => (_parent = p),

  /** Non-query version to enrich customer config object with inheritance (Default -> Organization -> Customer).
   * Please use this EVERYWHERE you need to access the customer config object.
   * @param {Object} customerConfig - Internal customer config object.
   * @param {Object} organizationConfig - Internal organization config object.
   * @return {Object} Return merged config jsonb.
   */
  enrichCustomerConfig: function (customerConfig = {}, organizationConfig = {}) {
    // Validate config objects
    if (customerConfig && typeof customerConfig !== `object`) throw new Error(`The customerConfig must be an object!`);
    if (organizationConfig && typeof organizationConfig !== `object`)
      throw new Error(`The organizationConfig must be an object!`);

    // Initialize mutable config objects
    let mutableCustomerConfig = JSON.parse(JSON.stringify(customerConfig)) || {};
    let mutableOrganizationConfig = JSON.parse(JSON.stringify(organizationConfig)) || {};

    // Create the config inheritance by deepmerging them (Default -> Organization -> Customer)
    let mergedConfig = deepmerge(defaultConfig, mutableOrganizationConfig, { arrayMerge: (dst, src) => src });
    mergedConfig = deepmerge(mergedConfig, mutableCustomerConfig, { arrayMerge: (dst, src) => src });

    // Return the merged config
    return mergedConfig;
  },

  /** Non-query version to enrich organization config object with inheritance (Default -> Organization).
   * Please use this EVERYWHERE you need to access the organization config object.
   * @param {Object} organizationConfig - Internal organization config object.
   * @return {Object} Return merged config jsonb.
   */
  enrichOrganizationConfig: function (organizationConfig = {}) {
    // Validate config objects
    if (organizationConfig && typeof organizationConfig !== `object`)
      throw new Error(`The organizationConfig must be an object!`);

    // Initialize mutable config objects
    let mutableOrganizationConfig = JSON.parse(JSON.stringify(organizationConfig)) || {};

    // Create the config inheritance by deepmerging them (Default -> Organization)
    let mergedConfig = deepmerge(defaultConfig, mutableOrganizationConfig, { arrayMerge: (dst, src) => src });

    // Return the merged config
    return mergedConfig;
  },

  /** Query version to fetch customer config object with inheritance (Default -> Organization -> Customer).
   * @param {Int} customerId - Internal customer id.
   * @return {Object} Return merged config jsonb.
   */
  getCustomerConfig: async function (customerId) {
    // Validate customerId
    if (!customerId) throw new Error(`No customerId found!`);
    if (typeof customerId !== `number`) throw new Error(`The customerId must be an integer!`);

    // Initialize config objects
    let customerConfig = {};
    let organizationConfig = {};

    // Query the customer & organization config
    try {
      const res = await _parent.gql.query(
        gql`
          query sdk_getCustomerConfig($customerId: bigint!) {
            customers(where: { id: { _eq: $customerId } }) {
              id
              config
              organization {
                id
                config
              }
            }
          }
        `,
        { customerId }
      );

      // Check for errors & validate the response
      if (!res || !res.success || !res.data || !res.data.length || !Array.isArray(res.data))
        throw new Error(`Errors in gql response!`);

      // Set the customer & organization configs
      customerConfig = res.data[0].config || {};
      if (res.data[0].organization) organizationConfig = res.data[0].organization.config || {};
    } catch (err) {
      console.error(`Failed to fetch customer & organization config:`, err);
      throw err;
    }

    // Return the merged config
    return _parent.configs.enrichCustomerConfig(customerConfig, organizationConfig);
  },

  /** Query version to fetch organization config object with inheritance (Default -> Organization).
   * @param {Int} organizationId - Internal organization id.
   * @return {Object} Return merged config jsonb.
   */
  getOrganizationConfig: async function (organizationId) {
    // Validate organizationId
    if (!organizationId) throw new Error(`No organizationId found!`);
    if (typeof organizationId !== `number`) throw new Error(`The organizationId must be an integer!`);

    // Initialize config object
    let organizationConfig = {};

    // Query the organization config
    try {
      const res = await _parent.gql.query(
        gql`
          query sdk_getOrganizationConfig($organizationId: bigint!) {
            organizations(where: { id: { _eq: $organizationId } }) {
              id
              config
            }
          }
        `,
        { organizationId }
      );

      // Check for errors & validate the response
      if (!res || !res.success || !res.data || !res.data.length || !Array.isArray(res.data))
        throw new Error(`Errors in gql response!`);

      // Set the organization config
      organizationConfig = res.data[0].config || {};
    } catch (err) {
      console.error(`Failed to fetch organization config:`, err);
      throw err;
    }

    // Return the merged config
    return _parent.configs.enrichOrganizationConfig(organizationConfig);
  },

  /** Build list of potential private options from workflow steps
   * @return {Object} - Object of private settings
   * @param {bigint} customerId - ID of the customer
   * @param {bigint} organizationId - ID of the organization
   * @param {String} type - Type of the entity (organization or customer)
   */

  getPrivateSettings: async function (customerId = null, organizationId = null, type) {
    if (type === 'organization' && !organizationId) {
      console.log('Organization ID is required for organization type');
      return null;
    }
    if (type === 'customer' && (!customerId || !organizationId)) {
      console.log('Customer ID and Organization ID are required for customer type');
      return null;
    }

    if (!type || (type !== 'organization' && type !== 'customer')) {
      console.log('Invalid type');
      return null;
    }

    const organizationConfig = await _parent.configs.getOrganizationConfig(organizationId);
    const customerConfig = customerId ? await _parent.configs.getCustomerConfig(customerId) : {};
    let workflowsetRes;
    try {
      if (type === 'organization') {
        workflowsetRes = await _parent.gql.query(
          gql`
            query sdk_getOrganizationWorkflows($id: bigint!) {
              workflowsets(where: { organization_id: { _eq: $id } }) {
                pickupWorkflow {
                  steps
                }
                deliveryWorkflow {
                  steps
                }
                fuelWorkflow {
                  steps
                }
              }
            }
          `,
          { id: organizationId }
        );
      } else {
        workflowsetRes = await _parent.gql.query(
          gql`
            query sdk_getCustomerWorkflows($customer_id: bigint!, $organization_id: bigint!) {
              workflowsets(
                where: { _or: [{ customer_id: { _eq: $customer_id } }, { organization_id: { _eq: $organization_id } }] }
              ) {
                pickupWorkflow {
                  steps
                }
                deliveryWorkflow {
                  steps
                }
                fuelWorkflow {
                  steps
                }
              }
            }
          `,
          { customer_id: customerId, organization_id: organizationId }
        );
      }
    } catch (err) {
      console.error(`Failed to fetch organization workflows:`, err);
      return null;
    }

    const getPrivateSteps = (pickupWorkflowSteps, deliveryWorkflowSteps, fuelWorkflowSteps) => {
      let steps = pickupWorkflowSteps.concat(deliveryWorkflowSteps, fuelWorkflowSteps);
      let privateSteps = [];

      steps.forEach(step => {
        if (step.config && step.config.private && (step.config.private === true || step.config.private === 'true')) {
          privateSteps.push(step.id.replace(/-/g, '_'));
        } else if (step && step.steps && step.steps.length > 0) {
          step.steps.forEach(subStep => {
            if (
              subStep &&
              subStep.config &&
              subStep.config.private &&
              (subStep.config.private === true || subStep.config.private === 'true')
            ) {
              privateSteps.push(subStep.id.replace(/-/g, '_'));
            }
          });
        }
      });

      return privateSteps;
    };

    const getPrivateOptions = (privateSteps = [], config = {}) => {
      let privateSettings = {};
      if (config && config.private && Object.keys(config.private).length > 0) {
        privateSteps.forEach(step => {
          if (config.private.hasOwnProperty(step)) {
            privateSettings[step] = config.private[step];
          } else privateSettings[step] = true;
        });
      } else {
        privateSteps.forEach(step => {
          privateSettings[step] = true;
        });
      }
      return privateSettings;
    };

    if (workflowsetRes && workflowsetRes.data && workflowsetRes.data.length > 0) {
      let pickupWorkflowSteps =
        workflowsetRes.data[0].pickupWorkflow && workflowsetRes.data[0].pickupWorkflow.steps
          ? workflowsetRes.data[0].pickupWorkflow.steps
          : [];
      let deliveryWorkflowSteps =
        workflowsetRes.data[0].deliveryWorkflow && workflowsetRes.data[0].deliveryWorkflow.steps
          ? workflowsetRes.data[0].deliveryWorkflow.steps
          : [];
      let fuelWorkflowSteps =
        workflowsetRes.data[0].fuelWorkflow && workflowsetRes.data[0].fuelWorkflow.steps
          ? workflowsetRes.data[0].fuelWorkflow.steps
          : [];
      let privateSteps = getPrivateSteps(pickupWorkflowSteps, deliveryWorkflowSteps, fuelWorkflowSteps);
      if (privateSteps && privateSteps.length > 0) {
        let privateSettings = {};
        let enrichedOrgConfig = organizationConfig;
        if (type === 'organization') {
          privateSettings = getPrivateOptions(privateSteps, organizationConfig);
          enrichedOrgConfig = deepmerge(organizationConfig, { private: privateSettings });
        } else {
          let enrichedCustomerConfig = _parent.configs.enrichCustomerConfig(customerConfig, enrichedOrgConfig);
          privateSettings = getPrivateOptions(privateSteps, enrichedCustomerConfig);
        }
        return privateSettings;
      } else {
        return null;
      }
    } else {
      return null;
    }
  },

  /** Get the default config object
   * @return {Object} Return default config jsonb
   */
  getDefaultConfig: function () {
    return defaultConfig;
  },

  /** Save customer config object.
   * @param {Int} customerId - Internal customer id.
   * @param {Object} config - Customer config object to save.
   * @return {Object} Return saved customer config object.
   */
  saveCustomerConfig: async function (customerId, config) {
    // Validate customerId & config
    if (!customerId) throw new Error(`No customerId found!`);
    if (typeof customerId !== `number`) throw new Error(`The customerId must be an integer!`);
    if (!config || typeof config !== `object`) throw new Error(`No config object found!`);

    // Save the customer config
    try {
      const res = await _parent.gql.mutate(
        gql`
          mutation sdk_saveCustomerConfig($customerId: bigint!, $config: jsonb!) {
            update_customers(where: { id: { _eq: $customerId } }, _set: { config: $config }) {
              affected_rows
            }
          }
        `,
        { customerId, config }
      );

      // Check for errors & validate the response
      if (!res || !res.success || !res.data || !res.data.update_customers || !res.data.update_customers.affected_rows)
        throw new Error(`Errors in gql response!`);
    } catch (err) {
      console.error(`Failed to save customer config:`, err);
      throw err;
    }

    // Return the saved config object
    return config;
  },

  /** Save organization config object.
   * @param {Int} organizationId - Internal organization id.
   * @param {Object} config - Organization config object to save.
   * @return {Object} Return saved organization config object.
   */
  saveOrganizationConfig: async function (organizationId, config) {
    // Validate organizationId & config
    if (!organizationId) throw new Error(`No organizationId found!`);
    if (typeof organizationId !== `number`) throw new Error(`The organizationId must be an integer!`);
    if (!config || typeof config !== `object`) throw new Error(`No config object found!`);

    // Save the organization config
    try {
      const res = await _parent.gql.mutate(
        gql`
          mutation sdk_saveOrganizationConfig($organizationId: bigint!, $config: jsonb!) {
            update_organizations(where: { id: { _eq: $organizationId } }, _set: { config: $config }) {
              affected_rows
            }
          }
        `,
        { organizationId, config }
      );

      // Check for errors & validate the response
      if (
        !res ||
        !res.success ||
        !res.data ||
        !res.data.update_organizations ||
        !res.data.update_organizations.affected_rows
      )
        throw new Error(`Errors in gql response!`);
    } catch (err) {
      console.error(`Failed to save organization config:`, err);
      throw err;
    }

    // Return the saved config object
    return config;
  },

  /** Reset/remove a property on the customer/org config.
   * When you "reset" or "remove" a property, you are essentially deleting it from the config object and relying on the parent to supply a value instead.
   * @param {Object} config - A customer/org config object to reset a property on.
   * @param {String} property - Property to reset (separated by dots, Ex: "add_moves_always" or "branding.logo_url").
   * @return {Object} Return the modified config object.
   */
  resetConfigProperty: function (config, property) {
    // Validate config & property
    if (!config || typeof config !== `object`) throw new Error(`No config object found!`);
    if (!property || typeof property !== `string`) throw new Error(`No property found!`);

    // Create a mutable copy of the config object
    let newConfig = JSON.parse(JSON.stringify(config));

    // Split out the keys and reduce to get the final nested key, then delete it
    let keys = property.split(`.`);
    let last = keys.pop();
    delete keys.reduce((obj, key) => obj[key], newConfig)[last];

    // Delete empty config objects if necessary
    Object.keys(newConfig).forEach(key => {
      if (typeof newConfig[key] === `object` && Object.keys(newConfig[key]).length === 0) {
        delete newConfig[key];
      }
    });

    // Return the modified config object
    return newConfig;
  },
};

module.exports = config;
